/**
 * Сетка отступов и радиусов — те же числа для всего приложения.
 *
 * <p><b>Что было.</b> В стилях сходились отступы 1, 2, 3, 4, 5, 6, 8, 10, 12, 14, 16, 18, 20,
 * 24, 26, 28, 32, 46, 48, 52 и двадцать семь разных радиусов — от 1 до 110. Каждое число
 * подбиралось на глаз под конкретное место и по отдельности выглядело осмысленным; рядом
 * друг с другом они давали разнобой, который не читается как ошибка, но и не складывается в
 * ритм. Строка списка с `paddingVertical: 9` — характерный след такого подбора.
 *
 * <p><b>Почему шаг 4.</b> Обе платформы считают в точках, кратных четырём: у Material сетка
 * 4dp, у HIG — 8pt с половинным шагом. Значения между ступенями (9, 10, 14, 18, 26) не дают
 * ничего, чего не даёт соседняя ступень, зато лишают экран общей вертикальной решётки.
 *
 * <p>Здесь только числа и ни одного цвета — как в `controlMetrics.ts`, откуда взята геометрия
 * кнопок. Эти два файла не пересекаются: там размеры интерактивных элементов, здесь просветы
 * между ними и скругления поверхностей.
 */

/**
 * Ступени отступа. Именование по t-shirt, а не по числу: значение ступени может однажды
 * поменяться (например, `lg` уедет с 16 на 20 при переходе на более просторную вёрстку), и
 * тогда `SPACE.lg` останется верным, а `SPACE.s16` пришлось бы переименовывать по всему коду.
 */
export const SPACE = {
  /** Волосяной просвет: разрыв между иконкой и её бейджем, вертикальный сдвиг на пиксель-два. */
  hair: 2,
  /** Минимальный настоящий отступ: между строками одного блока, вокруг мелкого бейджа. */
  xs: 4,
  /** Рабочая единица: просвет внутри строки, между иконкой и подписью, между соседними чипами. */
  sm: 8,
  /** Внутренние поля карточки и просвет между её строками. */
  md: 12,
  /** Поля экрана и крупных панелей, разрыв между смысловыми блоками. */
  lg: 16,
  /** Разрыв между разделами: между шапкой и списком, между группами настроек. */
  xl: 24,
  /** Крупная пауза: пустое состояние, отступ до кнопки внизу модалки. */
  xxl: 32,
} as const;

/**
 * Скругления поверхностей.
 *
 * <p>Интерактивные элементы сюда НЕ ходят: у кнопки радиус один на всё приложение и живёт в
 * `CONTROL.radius`. Здесь скругления того, на чём кнопки лежат, — карточек, панелей, модалок,
 * пузырей, — плюс капсула для бейджей-счётчиков.
 */
export const RADIUS = {
  /** Мелкая плашка внутри карточки: превью ответа, вложение, цветная метка. */
  sm: 8,
  /** Карточка списка и строка внутри панели. */
  md: 12,
  /** Крупная поверхность: модальная карточка, лист, панель дружины. */
  lg: 16,
  /** Капсула — бейдж-счётчик, таймер, пилюля статуса. Форму задаёт высота, а не число. */
  pill: 999,
} as const;
